import React, { useState, useEffect } from 'react';
import { useOrg } from '../../../hooks/useOrg';
import { api } from '../../../lib/api';
import ConfirmDialog from '../../shared/ConfirmDialog';

const PRESET_COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#223368',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#0DA7CA',
  '#DC2626', '#84CC16', '#A855F7', '#0EA5E9', '#6B7280',
];

export default function AppointmentTypesTab() {
  const { currentOrg } = useOrg();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ label: '', color: '#0DA7CA', default_charge: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTypes = async () => {
    if (!currentOrg) return;
    try {
      const data = await api.getAppointmentTypes(currentOrg.id);
      setTypes(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTypes(); }, [currentOrg]); // eslint-disable-line

  const resetForm = () => { setForm({ label: '', color: '#0DA7CA', default_charge: '' }); setShowAdd(false); setEditId(null); setError(''); };

  const startEdit = (t) => { setEditId(t.id); setForm({ label: t.label, color: t.color, default_charge: t.default_charge ?? '' }); setShowAdd(false); setError(''); };

  const handleSave = async () => {
    if (!form.label.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.updateAppointmentType(currentOrg.id, editId, { label: form.label, color: form.color, default_charge: form.default_charge });
      } else {
        const key = form.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
        await api.createAppointmentType(currentOrg.id, { key, label: form.label, color: form.color, sort_order: types.length, default_charge: form.default_charge });
      }
      resetForm();
      await fetchTypes();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await api.deleteAppointmentType(currentOrg.id, confirmDelete.id);
    setConfirmDelete(null);
    await fetchTypes();
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';
  const isEditing = showAdd || editId;

  return (
    <>
      {/* Add/Edit form */}
      {isEditing && (
        <div className="bg-elevated border border-theme rounded-xl p-5 mb-4 animate-fade-in">
          <h3 className="text-[15px] font-semibold text-primary-c mb-4">{editId ? 'Edit Type' : 'New Appointment Type'}</h3>
          {error && <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium mb-4">{error}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Name</label>
              <input type="text" className={inputClass} value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g., MRI" autoFocus />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Usual charge ($)</label>
              <input type="number" min="0" step="0.01" className={inputClass} value={form.default_charge} onChange={e => setForm(p => ({ ...p, default_charge: e.target.value }))} placeholder="e.g., 425 — pre-fills visit billing" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                    className={`w-7 h-7 rounded-lg transition-all hover:scale-110 ${form.color === c ? 'ring-2 ring-offset-2 ring-offset-[var(--bg-elevated)] scale-110' : ''}`}
                    style={{ background: c, ringColor: c }} />
                ))}
                <input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent" />
              </div>
            </div>
            <div className="flex items-center gap-3 bg-tertiary p-3 rounded-[10px] border border-theme">
              <div className="w-1 h-6 rounded-full" style={{ background: form.color }} />
              <span className="text-[12px] font-semibold px-3 py-1 rounded-full text-white" style={{ background: form.color }}>{form.label || 'Preview'}</span>
            </div>
            <div className="flex justify-end gap-2.5 pt-3 border-t border-theme">
              <button onClick={resetForm} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50">
                {saving ? 'Saving...' : editId ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Types list */}
      <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme">
          <span className="text-[13px] font-semibold text-secondary-c">{types.length} type{types.length !== 1 ? 's' : ''}</span>
          {!isEditing && (
            <button onClick={() => { setShowAdd(true); setEditId(null); setForm({ label: '', color: '#0DA7CA', default_charge: '' }); }}
              className="flex items-center gap-1.5 text-[12px] font-semibold text-accent-light hover:text-accent transition-colors">
              <span className="text-[15px] leading-none">+</span> Add Type
            </button>
          )}
        </div>
        {loading ? (
          <div className="px-5 py-8 text-center text-[13px] text-muted-c">Loading...</div>
        ) : types.length === 0 ? (
          <div className="px-5 py-12 text-center text-[13px] text-muted-c">No appointment types yet.</div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {types.map(t => (
              <div key={t.id} className="flex items-center justify-between px-5 py-3 hover:bg-hover-surface transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: t.color, boxShadow: `0 0 8px ${t.color}40` }} />
                  <span className="text-[13px] font-semibold text-primary-c">{t.label}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(t)} className="text-[12px] font-medium text-muted-c hover:text-accent-light transition-colors">Edit</button>
                  <button onClick={() => setConfirmDelete(t)} className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog open={!!confirmDelete} title="Delete Appointment Type"
        message={`Delete "${confirmDelete?.label}"? Existing appointments will keep their type.`}
        confirmLabel="Delete" danger onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} />
    </>
  );
}
